// src/pages/Creatives.jsx
import React from 'react';
import { Box, Typography, Button, Container, Grid, Card, CardContent, Link } from '@mui/material';

// const brandYellow = '#FCCB26';

const Section = ({ title, children, bg, color }) => (
  <Box py={8} bgcolor={bg || 'white'} color={color || 'black'}>
    <Container>
      {title && (
        <Typography variant="h4" fontWeight="bold" mb={4} textAlign="center">
          {title}
        </Typography>
      )}
      {children}
    </Container>
  </Box>
);

// #ee826c
// #f7a898
// #f5c5bb
// #f7dfdb

const Creatives = () => {
  return (
    <Box>

      {/* Hero Section with Background Image and Blur */}
      <Box
        sx={{
          position: 'relative',
          height: '95vh',
          color: 'white',
          overflow: 'hidden',
        }}
      >
        {/* Background Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'url(/assets/servicesPic/creatives1.jpg)', // ← Replace with your actual image
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.5) blur(2px)',
            zIndex: 0,
          }}
        />

        {/* Content Over Background */}
        <Box
          sx={{
            position: 'relative',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            zIndex: 1,
            textAlign: 'center',
            px: 3,
          }}
        >
          <Typography variant="h2" fontWeight="bold" mb={2}>
            Higher Low for Creatives
          </Typography>
          <Typography variant="h5" sx={{ color: '#FCCB26' }} gutterBottom>
            Support. Vision. Execution.
          </Typography>
          <Typography variant="body1" maxWidth="md" fontSize={20}>
            You bring the story. We bring the system. Whether you're a director, actor, musician, writer, or independent creative — Higher Low is a production home built for people like you.
          </Typography>
        </Box>
      </Box>



      {/* What We Do Section */}
      <Section title="What We Do for Creatives" bg="#f7dfdb">
        <Grid container spacing={4}>
          {[
            ['Visual Storytelling', 'Music videos, narrative shorts, concept pieces — produced end-to-end or with your vision leading.'],
            ['Actor & Artist Showcases', 'Performance reels, visual portfolios, and original scenes shot with care and cinema-level production.'],
            ['Production Support', 'From one-day shoots to multi-phase productions — we provide crew, gear, direction, and structure.'],
            ['Creative Development', 'Need help sharpening your concept? We collaborate on ideas, scripts, pitch decks, and direction.'],
            ['Festival & Distribution-Ready', 'Select works are developed with a pathway to festivals, press, and distribution in mind.']
          ].map(([title, desc], idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Box p={3} bgcolor="#ee826c" borderRadius={2} boxShadow={2}>
                <Typography variant="h6" fontWeight="bold">{title}</Typography>
                <Typography variant="body2">{desc}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* How It Works Section */}
      <Section title="How It Works" bg="#f7a898">
        <Grid container spacing={4}>
          {[
            ['Submit Your Project or Idea', 'No need for a full deck or budget. A rough idea or sample is enough to start.'],
            ['Collaborate with Our Team', 'We match you with the right people — cinematographers, editors, designers.'],
            ['Build Something Real', 'We produce or support depending on what you need. Transparent terms, clear timelines.']
          ].map(([title, desc], idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Box p={2} bgcolor="#f5c5bb" borderRadius={2} boxShadow={1}>
                <Typography variant="h6" fontWeight="bold">{title}</Typography>
                <Typography variant="body2">{desc}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* Why Higher Low Section (Improved) */}
      <Section title="Why Higher Low?" bg="#f5c5bb">
        <Grid container spacing={3}>
          {[
            'A creative-first approach — no pressure to “sell” or “go viral”',
            'Built-in community across disciplines',
            'Affordable entry points with professional output',
            'Transparent, respectful, no-ego culture',
            'Potential for long-term collaboration, exposure, and growth'
          ].map((text, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card elevation={7} sx={{ height: '100%', borderRadius: 2, backgroundColor: '#f7a898' }}>
                <CardContent>
                  <Typography variant="body1" fontWeight="medium">
                    {text}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* Call To Action Section (Centered + Popped) */}
      <Section bg="#f7dfdb">
        <Box textAlign="center">
          <Typography variant="h4" fontWeight="bold" mb={3}>
            Start Something
          </Typography>
          <Typography variant="h6" mb={4}>
            If you’re ready to make, grow, or test something creatively — we’re here.
          </Typography>
          <Box display="flex" justifyContent="center" gap={3} flexWrap="wrap" mb={4}>
            <Button
              component="a"
              href="mailto:shahrooz@higherlowfilms.com?subject=Project Brief Submission"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="large"
              sx={{
                bgcolor: '#ee826c',
                color: 'black',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                boxShadow: 4,
                '&:hover': { bgcolor: '#f7a898' },
              }}
            >
              Submit Your Project Brief
            </Button>

            <Button
              component="a"
              href="https://calendly.com/higherlowfilms/30min"
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#ee826c',
                color: '#ee826c',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': { borderColor: '#f7a898', color: '#f7a898' },
              }}
            >
              Book a Free Call
            </Button>
          </Box>

          <Typography variant="body1">
                    👀 Want to explore other paths? Check out our work with{' '}
                        <Link href="/services/studios" underline="hover" fontWeight="bold">
                            Studios
                        </Link>{' '}
                        –{' '}
                        <Link href="/services/brands" underline="hover" fontWeight="bold">
                            Brands
                        </Link>{' '}
                        –{' '}
                        <Link href="/services/supports" underline="hover" fontWeight="bold">
                            Supports
                        </Link>{' '}
                        →
                    </Typography>

        </Box>
        <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 4,
          flexWrap: 'wrap',
          mt: 6,
          mb: 8, // space from footer
          px: 2,
        }}
      >
        <Box
          sx={{
            width: 500,
            height: 360,
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: 3,
            backgroundColor: '#eee',
            backgroundImage: 'url(/assets/servicesPic/creatives.jpg)', // Replace with actual image
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        <Box
          sx={{
            width: 500,
            height: 360,
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: 3,
            backgroundColor: '#eee',
            backgroundImage: 'url(/assets/servicesPic/creatives2.jpg)', // Replace with actual image
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      </Box>
      </Section>

      



    </Box>


  );
};

export default Creatives;
